import { StorageKeys, clearBoardScopedStorage } from '../constants/storageKeys';
import axios, { AxiosResponse } from 'axios';
import { logger } from '../utils/logger';
import {
  CreateUserRequest,
  RegisterResponse,
  LoginRequest,
  LoginResponse,
  CreateRetrospectiveRequest,
  CreateRetrospectiveResponse,
  CreateCardRequest,
  CreateCardResponse,
  UpdateCardRequest,
  DeleteCardRequest,
  AddParticipantRequest,
  AddParticipantResponse,
  GetRetrospectivesResponse,
  GetUserRetrospectivesRequest,
  GetSingleRetrospectiveRequest,
  GetSingleRetrospectiveResponse,
  FinishRetrospectiveRequest,
  RegenerateInviteTokenRequest,
  RegenerateInviteTokenResponse,
  // Poker types
  CreatePokerSessionRequest,
  CreatePokerSessionResponse,
  GetUserPokerSessionsRequest,
  GetUserPokerSessionsResponse,
  JoinPokerSessionRequest,
  JoinPokerSessionResponse,
  StartRoundRequest,
  StartRoundResponse,
  SubmitVoteRequest,
  SubmitVoteResponse,
  RevealVotesRequest,
  RevealVotesResponse,
  SetFinalScoreRequest,
  GetPokerSessionRequest,
  GetPokerSessionResponse
} from '../types/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send httpOnly auth cookies with every request
});

// Authenticated users' tokens are in httpOnly cookies — only inject guest tokens (sessionStorage, tab-specific)
apiClient.interceptors.request.use(
  (config) => {
    if (!config.headers.Authorization) {
      const guestStr = sessionStorage.getItem(StorageKeys.GUEST);
      if (guestStr) {
        try {
          const guest = JSON.parse(guestStr);
          if (guest.token) {
            config.headers.Authorization = `Bearer ${guest.token}`;
          }
        } catch (error) {
          logger.error('[API] Error parsing guest from sessionStorage:', error);
        }
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Callback invoked when auth fails (set by AuthContext via setAuthFailureHandler)
let _authFailureHandler: (() => void) | null = null;
export const setAuthFailureHandler = (fn: () => void) => { _authFailureHandler = fn; };

function handleAuthFailure() {
  localStorage.removeItem(StorageKeys.USER);
  sessionStorage.removeItem(StorageKeys.GUEST);
  localStorage.removeItem(StorageKeys.BOARD_PARTICIPANTS);
  clearBoardScopedStorage();
  if (_authFailureHandler) {
    _authFailureHandler();
  } else {
    // Fallback before AuthContext registers the handler
    window.location.href = '/login';
  }
}

// Prevents multiple simultaneous token refresh requests
let isRefreshing = false;
let refreshSubscribers: Array<{ resolve: () => void; reject: (err: any) => void }> = [];

function subscribeTokenRefresh(resolve: () => void, reject: (err: any) => void) {
  refreshSubscribers.push({ resolve, reject });
}

function onTokenRefreshed() {
  refreshSubscribers.forEach(({ resolve }) => resolve());
  refreshSubscribers = [];
}

function onTokenRefreshFailed(err: any) {
  refreshSubscribers.forEach(({ reject }) => reject(err));
  refreshSubscribers = [];
}

// On 401, attempt a silent token refresh — tokens live in httpOnly cookies
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Auth endpoints return 401 for bad credentials — don't refresh on these,
      // it would silently retry the original request with a fresh token and still fail.
      if (originalRequest.url === '/auth/refresh' || originalRequest.url === '/auth/login') {
        if (originalRequest.url === '/auth/refresh') {
          logger.error('🚫 [API] Refresh token failed, logging out');
          handleAuthFailure();
        }
        return Promise.reject(error);
      }

      const userStr = localStorage.getItem(StorageKeys.USER);
      const guestStr = sessionStorage.getItem(StorageKeys.GUEST);

      // Only refresh for authenticated users — guests don't have refresh tokens
      if (!userStr || guestStr) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh(
            () => resolve(apiClient(originalRequest)),
            (err) => reject(err)
          );
        });
      }

      isRefreshing = true;

      try {
        logger.log('🔄 [API] Refreshing access token...');
        // Send empty body — refresh token is in httpOnly cookie
        await axios.post(
          `${API_BASE_URL}/api/auth/refresh`,
          {},
          {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true
          }
        );

        logger.log('✅ [API] Token refreshed successfully');

        onTokenRefreshed(); // Token is in httpOnly cookie — no value to pass
        isRefreshing = false;

        return apiClient(originalRequest);
      } catch (refreshError) {
        logger.error('🚫 [API] Token refresh failed:', refreshError);
        isRefreshing = false;
        onTokenRefreshFailed(refreshError); // Properly reject all waiting promises
        handleAuthFailure();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export class ApiService {
  static async createUser(data: CreateUserRequest): Promise<RegisterResponse> {
    logger.log('🔵 [API] Creating user:', { username: data.username, displayName: data.displayName });
    const response: AxiosResponse<RegisterResponse> = await apiClient.post('/auth/register', data);
    logger.log('✅ [API] User created successfully:', { userId: response.data.userId, displayName: response.data.displayName });
    return response.data;
  }

  static async login(data: LoginRequest): Promise<LoginResponse> {
    logger.log('🔵 [API] Login attempt:', { username: data.username });
    const response: AxiosResponse<LoginResponse> = await apiClient.post('/auth/login', data);
    logger.log('✅ [API] Login successful:', { userId: response.data.userId, displayName: response.data.displayName });
    return response.data;
  }

  static async logout(): Promise<void> {
    logger.log('🔵 [API] Logout attempt');
    await apiClient.post('/auth/logout');
    logger.log('✅ [API] Logout successful (cookies cleared)');
  }

  static async requestPasswordReset(data: { email: string }): Promise<{ resetToken?: string }> {
    logger.log('🔵 [API] Request password reset for email:', data.email);
    const response = await apiClient.post('/auth/forgot-password', { usernameOrEmail: data.email });
    logger.log('✅ [API] Password reset requested');
    return response.data ?? {};
  }

  static async resetPassword(data: { token: string; newPassword: string }): Promise<void> {
    logger.log('🔵 [API] Reset password with token');
    await apiClient.post('/auth/reset-password', data);
    logger.log('✅ [API] Password reset successful');
  }

  static async updateProfile(userId: string, data: { displayName?: string; username?: string; password?: string; currentPassword?: string }): Promise<void> {
    logger.log('🔵 [API] Update user profile:', userId);
    await apiClient.patch(`/users/${userId}`, data);
    logger.log('✅ [API] Profile updated');
  }

  static async createRetrospective(data: CreateRetrospectiveRequest): Promise<CreateRetrospectiveResponse> {
    logger.log('🔵 [API] Creating retrospective:', data);
    const response: AxiosResponse<CreateRetrospectiveResponse> = await apiClient.post('/retrospectives', data);
    logger.log('✅ [API] Retrospective created:', { retrospectiveId: response.data.retrospectiveId, inviteLink: response.data.inviteLink });
    return response.data;
  }

  static async getUserRetrospectives(data: GetUserRetrospectivesRequest): Promise<GetRetrospectivesResponse> {
    const params = new URLSearchParams();
    if (data.pageNumber) params.append('pageNumber', data.pageNumber.toString());
    if (data.pageSize) params.append('pageSize', data.pageSize.toString());
    if (data.sortBy) params.append('sortBy', data.sortBy);
    if (data.sortOrder) params.append('sortOrder', data.sortOrder);
    if (data.searchTerm) params.append('searchTerm', data.searchTerm);
    if (data.status) params.append('status', data.status);

    const response: AxiosResponse<GetRetrospectivesResponse> = await apiClient.get(
      `/retrospectives/user/${data.userId}/retrospectives${params.toString() ? '?' + params.toString() : ''}`
    );
    return response.data;
  }

  // Resolve a board id from a token-only invite link (/join/{token}), mirroring poker's join-by-token.
  static async resolveRetroInvite(inviteToken: string): Promise<{ retrospectiveId: string }> {
    const response: AxiosResponse<{ retrospectiveId: string }> = await apiClient.get(
      `/retrospectives/resolve-invite/${inviteToken}`
    );
    return response.data;
  }

  static async getSingleRetrospective(data: GetSingleRetrospectiveRequest): Promise<GetSingleRetrospectiveResponse> {
    logger.log('🔵 [API] Fetching retrospective:', data.retrospectiveId);
    const response: AxiosResponse<GetSingleRetrospectiveResponse> = await apiClient.get(`/retrospectives/${data.retrospectiveId}`);
    logger.log('✅ [API] Retrospective fetched:', {
      title: response.data.retrospective.title,
      status: response.data.retrospective.status,
      participants: response.data.retrospective.participants.length,
      cards: response.data.retrospective.cards.length
    });
    return response.data;
  }

  static async createCard(data: CreateCardRequest): Promise<CreateCardResponse> {
    logger.log('🔵 [API] Creating card:', { content: data.content, anonymous: data.isAnonymous, displayName: data.displayName });
    const response: AxiosResponse<CreateCardResponse> = await apiClient.post(`/retrospectives/${data.retrospectiveId}/cards`, data);
    logger.log('✅ [API] Card created:', { cardId: response.data.cardId });
    return response.data;
  }

  static async updateCard(data: UpdateCardRequest): Promise<void> {
    await apiClient.patch(`/retrospectives/${data.retrospectiveId}/cards/${data.cardId}`, data);
  }

  static async deleteCard(data: DeleteCardRequest): Promise<void> {
    await apiClient.delete(`/retrospectives/${data.retrospectiveId}/cards/${data.cardId}`);
  }

  static async addParticipant(data: AddParticipantRequest, token?: string): Promise<AddParticipantResponse> {
    logger.log('🔵 [API] Adding participant:', { displayName: data.displayName, retroId: data.retrospectiveId, hasToken: !!token });

    // Explicit headers override the interceptor (which would inject guest token instead)
    const config = token
      ? {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      : {};

    const response: AxiosResponse<AddParticipantResponse> = await apiClient.post(
      `/retrospectives/${data.retrospectiveId}/participants`,
      data,
      config
    );
    logger.log('✅ [API] Participant added:', { participantId: response.data.participantId });
    return response.data;
  }

  static async removeParticipant(retrospectiveId: string, participantId: string): Promise<void> {
    logger.log('👋 [API] Removing participant:', { retroId: retrospectiveId, participantId });
    await apiClient.delete(`/retrospectives/${retrospectiveId}/participants/${participantId}`);
    logger.log('✅ [API] Participant removed successfully');
  }

  static async finishRetrospective(data: FinishRetrospectiveRequest): Promise<void> {
    await apiClient.post(`/retrospectives/${data.retrospectiveId}/finish`);
  }

  static async revealCard(retrospectiveId: string, cardId: string): Promise<void> {
    await apiClient.post(`/retrospectives/${retrospectiveId}/cards/${cardId}/reveal`);
  }

  static async revealAllCards(retrospectiveId: string): Promise<void> {
    await apiClient.put(`/retrospectives/${retrospectiveId}/cards/reveal-all`);
  }

  static async toggleCardComplete(retrospectiveId: string, cardId: string): Promise<void> {
    await apiClient.post(`/retrospectives/${retrospectiveId}/cards/${cardId}/toggle-complete`);
  }

  static async setTimer(retrospectiveId: string, durationMinutes: number): Promise<void> {
    logger.log('⏰ [API] Setting timer:', { retroId: retrospectiveId, durationMinutes });
    await apiClient.post(`/retrospectives/${retrospectiveId}/timer`, { durationMinutes });
    logger.log('✅ [API] Timer set successfully');
  }

  static async stopTimer(retrospectiveId: string): Promise<void> {
    logger.log('⏹️ [API] Stopping timer:', { retroId: retrospectiveId });
    await apiClient.delete(`/retrospectives/${retrospectiveId}/timer`);
    logger.log('✅ [API] Timer stopped successfully');
  }

  static async checkTimerAndReveal(retrospectiveId: string): Promise<void> {
    logger.log('⏰ [API] Checking timer and revealing cards:', { retroId: retrospectiveId });
    await apiClient.post(`/retrospectives/${retrospectiveId}/check-timer`);
    logger.log('✅ [API] Timer check completed');
  }

  static async addCardReaction(retrospectiveId: string, cardId: string, userId: string, emoji: string): Promise<{ reactionId: string }> {
    logger.log('😊 [API] Adding card reaction:', { retroId: retrospectiveId, cardId, emoji });
    const response: AxiosResponse<{ reactionId: string }> = await apiClient.post(
      `/retrospectives/${retrospectiveId}/cards/${cardId}/reactions`,
      { userId, emoji }
    );
    logger.log('✅ [API] Reaction added:', { reactionId: response.data.reactionId });
    return response.data;
  }

  static async removeCardReaction(retrospectiveId: string, cardId: string, reactionId: string, userId: string): Promise<void> {
    logger.log('❌ [API] Removing card reaction:', { retroId: retrospectiveId, cardId, reactionId });
    await apiClient.delete(`/retrospectives/${retrospectiveId}/cards/${cardId}/reactions/${reactionId}?userId=${userId}`);
    logger.log('✅ [API] Reaction removed successfully');
  }

  static async addCardComment(retrospectiveId: string, cardId: string, userId: string, userDisplayName: string, content: string, imageUrl?: string, gifUrl?: string): Promise<{ commentId: string }> {
    logger.log('💬 [API] Adding card comment:', { retroId: retrospectiveId, cardId, imageUrl, gifUrl });
    const response: AxiosResponse<{ commentId: string }> = await apiClient.post(
      `/retrospectives/${retrospectiveId}/cards/${cardId}/comments`,
      { userId, userDisplayName, content, imageUrl, gifUrl }
    );
    logger.log('✅ [API] Comment added:', { commentId: response.data.commentId });
    return response.data;
  }

  static async updateCardComment(retrospectiveId: string, cardId: string, commentId: string, content: string): Promise<void> {
    logger.log('✏️ [API] Updating card comment:', { retroId: retrospectiveId, cardId, commentId });
    await apiClient.patch(
      `/retrospectives/${retrospectiveId}/cards/${cardId}/comments/${commentId}`,
      { content }
    );
    logger.log('✅ [API] Comment updated successfully');
  }

  static async deleteCardComment(retrospectiveId: string, cardId: string, commentId: string): Promise<void> {
    logger.log('🗑️ [API] Deleting card comment:', { retroId: retrospectiveId, cardId, commentId });
    await apiClient.delete(`/retrospectives/${retrospectiveId}/cards/${cardId}/comments/${commentId}`);
    logger.log('✅ [API] Comment deleted successfully');
  }

  static async regenerateInviteToken(data: RegenerateInviteTokenRequest): Promise<RegenerateInviteTokenResponse> {
    logger.log('🔄 [API] Regenerating invite token:', { retroId: data.retrospectiveId });
    const response: AxiosResponse<RegenerateInviteTokenResponse> = await apiClient.post(
      `/retrospectives/${data.retrospectiveId}/regenerate-invite`,
      { ownerId: data.ownerId }
    );
    logger.log('✅ [API] Invite token regenerated:', { newToken: response.data.newInviteToken, expiresAt: response.data.expiresAt });
    return response.data;
  }

  static async uploadFile(file: File): Promise<{ fileUrl: string; fileName: string; contentType: string; size: number }> {
    logger.log('📤 [API] Uploading file:', { fileName: file.name, size: file.size, type: file.type });

    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    logger.log('✅ [API] File uploaded successfully:', response.data);
    return response.data;
  }

  static async createPokerSession(data: CreatePokerSessionRequest): Promise<CreatePokerSessionResponse> {
    logger.log('🃏 [API] Creating poker session:', { title: data.title, ownerId: data.ownerId });
    const response: AxiosResponse<CreatePokerSessionResponse> = await apiClient.post('/poker-sessions', data);
    logger.log('✅ [API] Poker session created:', { sessionId: response.data.pokerSessionId, inviteLink: response.data.inviteLink });
    return response.data;
  }

  static async getPokerSession(data: GetPokerSessionRequest): Promise<GetPokerSessionResponse> {
    logger.log('🃏 [API] Fetching poker session:', { sessionId: data.pokerSessionId });
    const response: AxiosResponse<GetPokerSessionResponse> = await apiClient.get(`/poker-sessions/${data.pokerSessionId}`);
    logger.log('✅ [API] Poker session fetched:', { sessionId: response.data.pokerSession.pokerSessionId });
    return response.data;
  }

  // Resolve a poker session id from a token-only invite link, mirroring retro's resolveRetroInvite —
  // lets the join page navigate to the session even when the initial join call failed.
  static async resolvePokerInvite(inviteToken: string): Promise<{ pokerSessionId: string }> {
    const response: AxiosResponse<{ pokerSessionId: string }> = await apiClient.get(
      `/poker-sessions/resolve-invite/${inviteToken}`
    );
    return response.data;
  }

  static async joinPokerSession(data: JoinPokerSessionRequest, token?: string): Promise<JoinPokerSessionResponse> {
    logger.log('🃏 [API] Joining poker session:', { inviteToken: data.inviteToken, displayName: data.displayName, hasToken: !!token });
    // In the register/login-then-join flow the auth cookie isn't set yet, so pass the fresh token
    // explicitly as Bearer — otherwise the join is unauthenticated and (with userId in the body)
    // the backend rejects it as an expired-session join.
    const config = token
      ? { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      : {};
    const response: AxiosResponse<JoinPokerSessionResponse> = await apiClient.post(
      `/poker-sessions/join`,
      {
        inviteToken: data.inviteToken,
        displayName: data.displayName,
        userId: data.userId,
        guestSessionId: data.guestSessionId
      },
      config
    );
    logger.log('✅ [API] Joined poker session:', {
      pokerSessionId: response.data.pokerSessionId,
      participantId: response.data.participantId
    });
    return response.data;
  }

  static async startRound(data: StartRoundRequest): Promise<StartRoundResponse> {
    logger.log('🃏 [API] Starting round:', { sessionId: data.pokerSessionId, taskName: data.taskName });
    const response: AxiosResponse<StartRoundResponse> = await apiClient.post(
      `/poker-sessions/${data.pokerSessionId}/rounds`,
      {
        taskCode: data.taskCode,
        taskName: data.taskName
      }
    );
    logger.log('✅ [API] Round started:', { roundId: response.data.roundId });
    return response.data;
  }

  static async submitVote(data: SubmitVoteRequest): Promise<SubmitVoteResponse> {
    logger.log('🃏 [API] Submitting vote:', { roundId: data.roundId, score: data.score });
    const response: AxiosResponse<SubmitVoteResponse> = await apiClient.post(
      `/poker-sessions/${data.pokerSessionId}/votes`,
      {
        participantId: data.participantId,
        userId: data.userId,
        voterDisplayName: data.voterDisplayName,
        score: data.score
      }
    );
    logger.log('✅ [API] Vote submitted:', { voteId: response.data.voteId });
    return response.data;
  }

  static async revealVotes(data: RevealVotesRequest): Promise<RevealVotesResponse> {
    logger.log('🃏 [API] Revealing votes:', { roundId: data.roundId });
    const response: AxiosResponse<RevealVotesResponse> = await apiClient.post(
      `/poker-sessions/${data.pokerSessionId}/rounds/${data.roundId}/reveal`
    );
    logger.log('✅ [API] Votes revealed:', { consensusScore: response.data.consensusScore });
    return response.data;
  }

  static async setFinalScore(data: SetFinalScoreRequest): Promise<void> {
    logger.log('🃏 [API] Setting final score:', { roundId: data.roundId, finalScore: data.finalScore });
    await apiClient.post(
      `/poker-sessions/${data.pokerSessionId}/rounds/${data.roundId}/final-score`,
      { finalScore: data.finalScore }
    );
    logger.log('✅ [API] Final score set:', { finalScore: data.finalScore });
  }

  static async completePokerSession(pokerSessionId: string): Promise<void> {
    logger.log('🏁 [API] Completing poker session:', { pokerSessionId });
    await apiClient.post(`/poker-sessions/${pokerSessionId}/complete`);
    logger.log('✅ [API] Poker session completed');
  }

  static async kickPokerParticipant(pokerSessionId: string, participantId: string): Promise<void> {
    logger.log('🦵 [API] Kicking poker participant:', { pokerSessionId, participantId });
    await apiClient.delete(`/poker-sessions/${pokerSessionId}/participants/${participantId}`);
    logger.log('✅ [API] Poker participant kicked');
  }

  static async setPokerParticipantIdle(pokerSessionId: string, participantId: string, isIdle: boolean): Promise<void> {
    logger.log('☕ [API] Setting poker participant idle:', { pokerSessionId, participantId, isIdle });
    await apiClient.put(`/poker-sessions/${pokerSessionId}/participants/${participantId}/idle`, { isIdle });
    logger.log('✅ [API] Idle status updated');
  }

  static async getUserPokerSessions(data: GetUserPokerSessionsRequest): Promise<GetUserPokerSessionsResponse> {
    const params = new URLSearchParams();
    if (data.pageNumber) params.append('pageNumber', data.pageNumber.toString());
    if (data.pageSize) params.append('pageSize', data.pageSize.toString());
    if (data.sortBy) params.append('sortBy', data.sortBy);
    if (data.sortOrder) params.append('sortOrder', data.sortOrder);
    if (data.searchTerm) params.append('searchTerm', data.searchTerm);
    if (data.status) params.append('status', data.status);

    const response: AxiosResponse<GetUserPokerSessionsResponse> = await apiClient.get(
      `/poker-sessions/user/${data.userId}${params.toString() ? '?' + params.toString() : ''}`
    );
    return response.data;
  }

  static async searchGifs(query: string, limit: number = 20, offset: number = 0): Promise<GifSearchResponse> {
    const response: AxiosResponse<GifSearchResponse> = await apiClient.get(
      `/gif/search?q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`
    );
    return response.data;
  }

  static async getTrendingGifs(limit: number = 20, offset: number = 0): Promise<GifSearchResponse> {
    const response: AxiosResponse<GifSearchResponse> = await apiClient.get(
      `/gif/trending?limit=${limit}&offset=${offset}`
    );
    return response.data;
  }
}

// GIF types
export interface GifItem {
  id: string;
  title: string;
  url: string;
  preview: string;
}

export interface GifSearchResponse {
  results: GifItem[];
}

export default ApiService;